import React, { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Plus, Trash, PencilSimple, Rss } from "@phosphor-icons/react";
import { toast } from "sonner";

function Label({ children }) { return <label className="label-mono mb-1.5 block">{children}</label>; }

export default function AdminBlogs() {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);

  const load = () => api.get("/admin/blogs").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);

  const startCreate = () => {
    setEditing({ title: "", slug: "", excerpt: "", body: "", cover_image_url: "", tags: [], status: "draft", featured: false });
    setOpen(true);
  };
  const startEdit = (p) => { setEditing({ ...p, tags: p.tags || [] }); setOpen(true); };

  const save = async () => {
    try {
      if (editing.id) { await api.patch(`/admin/blogs/${editing.id}`, editing); }
      else { await api.post("/admin/blogs", editing); }
      toast.success("Saved");
      setOpen(false);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const remove = async (p) => {
    if (!window.confirm("Delete this post?")) return;
    await api.delete(`/admin/blogs/${p.id}`);
    load();
  };
  const fetchRss = async () => {
    try {
      const { data } = await api.post("/admin/blogs/fetch-rss");
      toast.success(`Created ${data.drafts_created} draft posts`);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div>
      <div className="border-b border-ink pb-8">
        <div className="flex items-end justify-between gap-3 flex-wrap">
          <div>
            <div className="label-mono">Admin · № 04</div>
            <h1 className="mt-3 font-display font-black text-4xl md:text-5xl tracking-tightest text-ink leading-[1]" data-testid="admin-blogs-title">
              Journal.
            </h1>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={fetchRss} data-testid="blog-fetch-rss">
              <Rss className="w-4 h-4 mr-1.5" /> Fetch sources
            </Button>
            <Button onClick={startCreate} data-testid="blog-new-btn">
              <Plus className="w-4 h-4 mr-1.5" weight="bold" /> New post
            </Button>
          </div>
        </div>
      </div>

      <div className="mt-8 grid md:grid-cols-2 gap-px bg-rule border border-rule">
        {rows.map((p) => (
          <div key={p.id} className="bg-white p-5" data-testid={`blog-admin-card-${p.slug}`}>
            <div className="flex items-center gap-2">
              <Badge variant="outline" className={p.status === "published" ? "border-signal/40 text-signal" : "border-gold/40 text-gold"}>
                {p.status}
              </Badge>
              {p.featured && <Badge>featured</Badge>}
            </div>
            <div className="mt-3 font-display text-lg font-bold tracking-tight text-ink leading-snug">{p.title}</div>
            <div className="mt-1 label-mono text-ink/40">/{p.slug}</div>
            <p className="mt-3 text-sm text-ink/65 line-clamp-2 leading-relaxed">{p.excerpt}</p>
            <div className="flex gap-2 mt-5">
              <Button size="sm" variant="outline" onClick={() => startEdit(p)} data-testid={`blog-edit-${p.slug}`}>
                <PencilSimple className="w-3.5 h-3.5 mr-1.5" />Edit
              </Button>
              <Button size="sm" variant="outline" onClick={() => remove(p)} data-testid={`blog-delete-${p.slug}`}
                className="text-alert border-alert/40 hover:bg-alert-bg">
                <Trash className="w-3.5 h-3.5 mr-1.5" />Delete
              </Button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto rounded-none border-ink no-round">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl tracking-tightest">{editing?.id ? "Edit post" : "New post"}</DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-5 pt-2">
              <div>
                <Label>Title</Label>
                <Input value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })}
                  data-testid="blog-title-input" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Slug · optional</Label>
                  <Input value={editing.slug || ""} onChange={(e) => setEditing({ ...editing, slug: e.target.value })} />
                </div>
                <div>
                  <Label>Cover image URL</Label>
                  <Input value={editing.cover_image_url || ""} onChange={(e) => setEditing({ ...editing, cover_image_url: e.target.value })} />
                </div>
              </div>
              <div>
                <Label>Excerpt</Label>
                <Textarea rows={2} value={editing.excerpt || ""} onChange={(e) => setEditing({ ...editing, excerpt: e.target.value })} />
              </div>
              <div>
                <Label>Body · markdown / plain text</Label>
                <Textarea rows={10} value={editing.body || ""} onChange={(e) => setEditing({ ...editing, body: e.target.value })}
                  data-testid="blog-body-input" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Status</Label>
                  <Select value={editing.status} onValueChange={(v) => setEditing({ ...editing, status: v })}>
                    <SelectTrigger data-testid="blog-status-select" className="rounded-none border-rule h-10"><SelectValue /></SelectTrigger>
                    <SelectContent className="rounded-none border-rule">
                      <SelectItem value="draft">Draft</SelectItem>
                      <SelectItem value="published">Published</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-end justify-between border border-rule px-4 py-2 h-10">
                  <Label>Featured</Label>
                  <Switch checked={!!editing.featured} onCheckedChange={(v) => setEditing({ ...editing, featured: v })} />
                </div>
              </div>
              <DialogFooter>
                <Button onClick={save} className="w-full" data-testid="blog-save-btn">
                  {editing.id ? "Update post" : "Create post"}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
